import {Alert, Button, Spin} from "antd";
import {useCallback, useEffect, useMemo, useState} from "react";
import {NavLink} from "react-router-dom";
import {setTitle} from "../../helpers/helper";
import {MemberVotingListItem} from "../../models/members/member-voting-list";
import ownerService from "../../services/owner-service";
import "./members-voting-list.scss";

const splitValues = (value: string | null | undefined): string[] => {
    if (value === null || value === undefined) {
        return [];
    }

    return value.split(";").map((item) => item.trim()).filter((item) => item !== "");
};

interface VotingTableRow {
    key: string;
    address: string;
    member: string;
    powerOfAttorney: string;
}

interface StreetGroup {
    street: string;
    rows: VotingTableRow[];
}

const addressComparator = new Intl.Collator("et", {numeric: true, sensitivity: "base"});

const createVotingTableRows = (items: MemberVotingListItem[]): VotingTableRow[] => {
    const rows: VotingTableRow[] = [];
    const attorneyRows = new Map<string, {member: string, addresses: Set<string>}>();

    items.forEach((item) => {
        rows.push({
            key: `owner-${item.ownerId}`,
            address: item.ownedProperties,
            member: item.owner,
            powerOfAttorney: "",
        });

        splitValues(item.attorneys).forEach((attorney) => {
            const separatorIndex = attorney.indexOf(": ");
            if (separatorIndex === -1) {
                return;
            }

            const address = attorney.slice(0, separatorIndex);
            const member = attorney.slice(separatorIndex + 2);
            const key = `${item.ownerId}:${member}`;
            const attorneyRow = attorneyRows.get(key) ?? {member, addresses: new Set<string>()};
            attorneyRow.addresses.add(address);
            attorneyRows.set(key, attorneyRow);
        });
    });

    attorneyRows.forEach(({member, addresses}, key) => {
        rows.push({
            key: `attorney-${key}`,
            address: Array.from(addresses).sort(addressComparator.compare).join("; "),
            member,
            powerOfAttorney: "X",
        });
    });

    return rows.sort((first, second) => addressComparator.compare(first.address, second.address));
};

const getStreetFromAddress = (address: string): string => {
    const addressParts = address.trim().split(/\s+/);
    const houseNumberIndex = addressParts.findIndex((part) => /^\d/.test(part));

    return houseNumberIndex > 0 ? addressParts.slice(0, houseNumberIndex).join(" ") : address;
};

const groupRowsByStreet = (rows: VotingTableRow[]): StreetGroup[] => {
    const groups: StreetGroup[] = [];

    rows.forEach((row) => {
        const street = getStreetFromAddress(row.address);
        const latestGroup = groups[groups.length - 1];

        if (latestGroup !== undefined && latestGroup.street === street) {
            latestGroup.rows.push(row);
            return;
        }

        groups.push({street, rows: [row]});
    });

    return groups;
};

const VotingTableRows = ({rows}: {rows: VotingTableRow[]}) => {
    if (rows.length === 0) {
        return (
            <tr>
                <td colSpan={4} className="members-voting-page__empty-row">No members found</td>
            </tr>
        );
    }

    return (
        <>
            {rows.map((row) => (
                <tr key={row.key}>
                    <td>{row.address}</td>
                    <td><strong>{row.member}</strong></td>
                    <td><strong>{row.powerOfAttorney}</strong></td>
                    <td></td>
                </tr>
            ))}
        </>
    );
};

export const MembersVotingList = () => {
    const [items, setItems] = useState<MemberVotingListItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const rows = useMemo(() => createVotingTableRows(items), [items]);
    const streetGroups = useMemo(() => groupRowsByStreet(rows), [rows]);

    const loadVotingList = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            const result = await ownerService.getMembersVotingList();
            setItems(result);
        } catch (requestError) {
            setError(requestError instanceof Error ? requestError.message : "Unable to load voting list");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        setTitle("Voting list");
        loadVotingList();
    }, [loadVotingList]);

    return (
        <main className="members-voting-page">
            <header className="members-voting-page__header">
                <NavLink to="/" className="members-voting-page__brand" aria-label="Kodus home">
                    <img src="/kodus_beta_logo.svg" alt="Kodus"/>
                </NavLink>
                <NavLink to="/members" className="members-voting-page__back-link">Members</NavLink>
            </header>

            <section className="members-voting-page__content" aria-labelledby="members-voting-title">
                <div className="members-voting-page__toolbar">
                    <div>
                        <p className="members-voting-page__eyebrow">Members</p>
                        <h1 id="members-voting-title">Voting list</h1>
                    </div>
                    <Button onClick={loadVotingList} loading={loading}>Refresh</Button>
                </div>

                {error !== null && <Alert message={error} type="error" showIcon/>}

                {loading ? (
                    <div className="members-voting-page__loading" aria-live="polite">
                        <Spin size="large"/>
                    </div>
                ) : error === null ? (
                    <div className="members-voting-page__table-scroll">
                        <table>
                            <colgroup>
                                <col width={"20%"}/>
                                <col width={"30%"}/>
                                <col width={"10%"}/>
                                <col width={"30%"}/>
                            </colgroup>
                            <thead>
                                <tr className="members-voting-page__print-table-header">
                                        <th colSpan={4}>
                                            <strong>NARVA AIANDUSÜHISTU RJABINKA (80038577), Kerese 15-43, 20309, Narva, Eesti</strong><br></br>
                                            <strong>Üldkoosoleku protokolli Lisa 1 18.07.2026</strong> / Дополнение 1 к протоколу общего собрания от 18.07.2026<br></br>
                                            <strong>Üldkoosoleku osalejate nimekiri 18.07.2026</strong> / Список присутствующих на общем собрании 18.07.2026<br></br><br></br>
                                        </th>
                                </tr>
                                <tr>
                                    <th scope="col"><strong>Aadress</strong> / Адрес</th>
                                    <th scope="col"><strong>Nimi, isikukood / Firma nimi, registrikood</strong> /
                                    Имя, личный код / Название фирмы, регистрационный код</th>
                                    <th scope="col"><strong>Volitus (X)</strong> / По доверенности (Х)</th>
                                    <th scope="col"><strong>Allkiri</strong> / Подпись</th>
                                </tr>
                            </thead>
                            {streetGroups.length === 0 ? (
                                <tbody><VotingTableRows rows={[]}/></tbody>
                            ) : streetGroups.map((group, index) => (
                                <tbody key={group.street} data-print-page-break={index === 0 ? undefined : "before"}>
                                    <VotingTableRows rows={group.rows}/>
                                </tbody>
                            ))}
                            <tfoot>
                                <tr className="members-voting-page__print-table-footer">
                                        <td colSpan={4}>
                                            <br></br><br></br>
                                            <strong>Üldkoosoleku kohalolijad kokku</strong> / присутствующих на общем собрании всего: ______________<br></br><br></br>
                                            Nendest volikirjatega / из них по доверенности: ______________<br></br><br></br>
                                            Protokollija / Протоколист:	______________<br></br><br></br>
                                            Üldkoosoleku esimees / Председатель собрания: ______________
                                        </td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                ) : null}
            </section>
        </main>
    );
};
